import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_EXT = ["jpg", "jpeg", "png"];
const ALLOWED_MIME = ["image/jpeg", "image/png"];

// Reusable image field: upload a JPG/JPEG/PNG (stored + valid public URL) OR paste an image URL.
export default function ImageUpload({ value, onChange, testid = "image-upload", privateFile = false }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext) || (f.type && !ALLOWED_MIME.includes(f.type))) {
      toast.error("Only JPG, JPEG or PNG images are allowed");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Image too large — maximum size is 10MB");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/uploads?private=${privateFile ? "true" : "false"}`, fd);
      const url = `${BACKEND_URL}${r.data.url}`;
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Image upload failed. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="grid gap-2" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-secondary">
          {value ? (
            <img src={value} alt="preview" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
          ) : (
            <div className="grid h-full w-full place-items-center text-muted-foreground"><Upload className="h-4 w-4" /></div>
          )}
        </div>
        <div className="grid flex-1 gap-1.5">
          <div className="flex gap-2">
            <button type="button" onClick={() => ref.current?.click()} disabled={busy}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-secondary disabled:opacity-60"
              data-testid={`${testid}-btn`}>
              {busy ? "Uploading..." : "Upload JPG / PNG"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="grid h-8 w-8 place-items-center rounded-md border border-border text-destructive hover:bg-destructive/10" data-testid={`${testid}-clear`}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" className="h-8 text-xs" data-testid={`${testid}-url`} />
        </div>
      </div>
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
    </div>
  );
}
